import React from "react";
import { Check, X } from "lucide-react";

/**
 * Apple-to-Apple normalized comparison matrix.
 * Rows = criteria · Columns = DMC bids · Highlight = best per row.
 */
const ROWS = [
  { key: "amount_usd", label: "Bid (USD)", fmt: (v) => `$${Number(v).toLocaleString()}`, best: "min" },
  { key: "timeline_days", label: "Delivery (days)", fmt: (v) => `${v}d`, best: "min" },
  { key: "hotel_category", label: "Hotel category", fmt: (v) => v || "—", best: "max-tier" },
  { key: "transfer_type", label: "Transfers", fmt: (v) => v || "—", best: "is-private" },
  { key: "meal_plan", label: "Meal plan", fmt: (v) => v || "—", best: "max-meal" },
  { key: "flight_class", label: "Flight class", fmt: (v) => (v === "not_included" ? "Not included" : v || "—"), best: "max-class" },
  { key: "apples_score", label: "AI Apples %", fmt: (v) => (v == null ? "—" : `${v}%`), best: "max" },
  { key: "ai_score", label: "AI Fit", fmt: (v) => (v == null ? "—" : v.toFixed(1)), best: "max" },
];

const HOTEL_RANK = { "3-star": 1, "4-star": 2, "5-star": 3, boutique: 2, luxury: 4 };
const MEAL_RANK = { "Room Only": 0, Breakfast: 1, "Half Board": 2, "Full Board": 3, "All Inclusive": 4 };
const CLASS_RANK = { not_included: 0, economy: 1, business: 2, first: 3 };

function bestIndex(rule, vals) {
  const idx = vals.map((v, i) => [v, i]).filter(([v]) => v != null && v !== "");
  if (!idx.length) return -1;
  if (rule === "min") return idx.reduce((a, b) => (Number(a[0]) <= Number(b[0]) ? a : b))[1];
  if (rule === "max") return idx.reduce((a, b) => (Number(a[0]) >= Number(b[0]) ? a : b))[1];
  if (rule === "is-private") {
    const p = idx.find(([v]) => String(v).toLowerCase() === "private");
    return p ? p[1] : -1;
  }
  const rank = rule === "max-tier" ? HOTEL_RANK : rule === "max-meal" ? MEAL_RANK : rule === "max-class" ? CLASS_RANK : null;
  if (rank) return idx.reduce((a, b) => (rank[a[0]] >= rank[b[0]] ? a : b))[1];
  return -1;
}

export default function ComparisonMatrix({ bids = [] }) {
  if (!bids.length) return null;
  // Show top 5 by AI score
  const sorted = [...bids].sort((a, b) => (b.ai_score || 0) - (a.ai_score || 0)).slice(0, 5);

  return (
    <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl overflow-hidden" data-testid="comparison-matrix">
      <div className="px-5 py-4 border-b border-[#1a1a1a] flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Apple-to-apple matrix</p>
          <h3 className="font-display text-white text-lg font-bold mt-1">Normalized bid comparison</h3>
        </div>
        <span className="text-xs font-mono text-[#737373] uppercase tracking-widest">
          {sorted.length} of {bids.length} bids
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="border-b border-[#1a1a1a] bg-[#080808]">
              <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-widest text-[#737373] sticky left-0 bg-[#080808]">
                Criteria
              </th>
              {sorted.map((b) => (
                <th key={b.bid_id} className="text-left py-3 px-4 min-w-[140px]">
                  <p className="font-display text-white font-bold text-sm truncate">{b.dmc_name}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#737373]">
                    {b.dmc_country || "—"} {b.dmc_premium && "· PREMIUM"}
                  </p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => {
              const values = sorted.map((b) => b[row.key]);
              const best = bestIndex(row.best, values);
              return (
                <tr key={row.key} className="border-b border-[#1a1a1a] hover:bg-[#0c0c0c]">
                  <td className="py-3 px-4 font-mono text-[11px] uppercase tracking-widest text-[#a3a3a3] sticky left-0 bg-[#0a0a0a]">
                    {row.label}
                  </td>
                  {sorted.map((b, i) => {
                    const isBest = i === best;
                    return (
                      <td
                        key={b.bid_id}
                        className={`py-3 px-4 text-sm ${isBest ? "text-brand font-bold bg-brand/5" : "text-white"}`}
                      >
                        {row.fmt(b[row.key])}
                        {isBest && <span className="ml-1.5 text-[9px] font-mono uppercase tracking-widest opacity-80">best</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Inclusions / Exclusions row */}
            <tr className="border-b border-[#1a1a1a]">
              <td className="py-3 px-4 font-mono text-[11px] uppercase tracking-widest text-[#a3a3a3] sticky left-0 bg-[#0a0a0a] align-top">
                Inclusions
              </td>
              {sorted.map((b) => (
                <td key={b.bid_id} className="py-3 px-4 align-top">
                  <ul className="space-y-1">
                    {(b.inclusions_list?.length ? b.inclusions_list : [b.inclusions].filter(Boolean)).slice(0, 6).map((s, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 text-xs text-[#d4d4d4]">
                        <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" /> <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </td>
              ))}
            </tr>
            <tr>
              <td className="py-3 px-4 font-mono text-[11px] uppercase tracking-widest text-[#a3a3a3] sticky left-0 bg-[#0a0a0a] align-top">
                Exclusions
              </td>
              {sorted.map((b) => (
                <td key={b.bid_id} className="py-3 px-4 align-top">
                  {b.exclusions_list?.length ? (
                    <ul className="space-y-1">
                      {b.exclusions_list.slice(0, 6).map((s, idx) => (
                        <li key={idx} className="flex items-start gap-1.5 text-xs text-amber-300">
                          <X className="w-3 h-3 flex-shrink-0 mt-0.5" /> <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-xs text-[#737373]">—</span>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
